(function () {
  'use strict';

  const STOCK_PROXY_URL = '/apps/check-stock';
  const CART_JSON_URL = '/cart.js';
  const TRACKING_PROPERTY = '_inventory_tracked';
  const LOADING_TEXT = 'a confirmar o stock dos equipamentos';
  const VERIFIED_TEXT = 'verificado';
  const SUCCESS_DELAY_MS = 350;
  const CHECKOUT_SELECTORS = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    'a[href$="/checkout"]',
    'a[href*="/checkout?"]',
    '.shopify-payment-button button',
    '.shopify-payment-button [role="button"]',
    '.shopify-payment-button__button',
    '.additional-checkout-buttons button',
    '.additional-checkout-buttons [role="button"]',
    '.payment-method-button',
    '.finalize-order-btn',
  ].join(',');

  let validating = false;
  let allowNextAction = false;
  let activeCheckoutControl = null;
  const stockCache = new Map();
  const loadingSnapshots = new Map();
  const diagnostics = [];

  window.InventoryCheckoutGuard = {
    getDiagnostics: () => [...diagnostics],
    clearDiagnostics: () => {
      diagnostics.length = 0;
    },
  };

  function recordDiagnostic(type, details = {}) {
    const entry = {
      type,
      at: new Date().toISOString(),
      ...details,
    };

    diagnostics.push(entry);
    console.info('[InventoryCheckoutGuard]', type, details);
    return entry;
  }

  function cartUpdateUrl() {
    return window.routes?.cart_update_url || '/cart/update.js';
  }

  function cartUrl() {
    return window.routes?.cart_url || '/cart';
  }

  function normalizeSku(sku) {
    return String(sku || '').trim();
  }

  function itemLabel(item) {
    const title = item.product_title || item.title || item.name || 'This item';
    const variant = item.variant_title && item.variant_title !== 'Default Title' ? ` - ${item.variant_title}` : '';
    const sku = normalizeSku(item.sku);

    return sku ? `${title}${variant} (SKU: ${sku})` : `${title}${variant} (missing SKU)`;
  }

  function readCartLineMetadata() {
    const byKey = new Map();
    const byVariantId = new Map();

    document.querySelectorAll('.cart-item[data-inventory-tracked]').forEach((element) => {
      const metadata = {
        inventoryTracked: element.dataset.inventoryTracked === 'true',
      };
      const cartKey = element.dataset.cartKey;
      const variantId = element.dataset.variantId;

      if (cartKey) byKey.set(cartKey, metadata);
      if (variantId && !byVariantId.has(String(variantId))) byVariantId.set(String(variantId), metadata);
    });

    return { byKey, byVariantId };
  }

  function parseTrackingValue(value) {
    if (value === true || value === 'true') return true;
    if (value === false || value === 'false') return false;
    return undefined;
  }

  function trackingFromLineProperties(item) {
    const properties = item.properties || {};
    return parseTrackingValue(properties[TRACKING_PROPERTY]);
  }

  function withCartLineMetadata(items) {
    const metadata = readCartLineMetadata();

    return items.map((item) => {
      const lineMetadata = metadata.byKey.get(item.key) || metadata.byVariantId.get(String(item.variant_id));
      const propertyTracking = trackingFromLineProperties(item);

      return {
        ...item,
        inventoryTracked: propertyTracking ?? lineMetadata?.inventoryTracked,
      };
    });
  }

  function shouldCheckExternalStock(item) {
    return item.inventoryTracked === false;
  }

  function checkoutControls() {
    return [...document.querySelectorAll(CHECKOUT_SELECTORS)];
  }

  function getMutableTextElement(control) {
    if (control.matches('input[type="submit"], input[type="button"]')) return control;
    if (control.querySelector('svg, img')) return null;
    return control.querySelector('span:not(.svg-wrapper)') || control;
  }

  function setControlText(control, text) {
    const textElement = getMutableTextElement(control);

    if (!textElement) return;
    if (textElement.matches('input')) {
      textElement.value = text;
      return;
    }

    textElement.textContent = text;
  }

  function ensureOverlay() {
    let overlay = document.getElementById('InventoryCheckoutGuardOverlay');

    if (overlay) return overlay;

    overlay = document.createElement('div');
    overlay.id = 'InventoryCheckoutGuardOverlay';
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');
    overlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:2147483647',
      'display:none',
      'align-items:center',
      'justify-content:center',
      'background:rgba(255,255,255,0.72)',
      'color:#111',
      'font:600 16px/1.4 system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
      'text-align:center',
      'cursor:wait',
    ].join(';');
    overlay.textContent = LOADING_TEXT;
    document.body.appendChild(overlay);

    return overlay;
  }

  function setOverlay(isVisible, text = LOADING_TEXT) {
    const overlay = ensureOverlay();

    overlay.textContent = text;
    overlay.style.display = isVisible ? 'flex' : 'none';
  }

  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function setCheckoutLoadingState(isLoading, activeControl) {
    checkoutControls().forEach((control) => {
      if (isLoading) {
        if (!loadingSnapshots.has(control)) {
          loadingSnapshots.set(control, {
            disabled: control.disabled,
            ariaDisabled: control.getAttribute('aria-disabled'),
            ariaBusy: control.getAttribute('aria-busy'),
            pointerEvents: control.style.pointerEvents,
            text:
              control.matches('input[type="submit"], input[type="button"]')
                ? control.value
                : getMutableTextElement(control)?.textContent,
          });
        }

        control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
        control.setAttribute('aria-busy', 'true');
        control.style.pointerEvents = 'none';
        control.classList.add('loading');

        if (control === activeControl) setControlText(control, LOADING_TEXT);
        setOverlay(true, LOADING_TEXT);
        return;
      }

      const snapshot = loadingSnapshots.get(control);
      if (!snapshot) return;

      control.disabled = snapshot.disabled;
      if (snapshot.ariaDisabled === null) {
        control.removeAttribute('aria-disabled');
      } else {
        control.setAttribute('aria-disabled', snapshot.ariaDisabled);
      }

      if (snapshot.ariaBusy === null) {
        control.removeAttribute('aria-busy');
      } else {
        control.setAttribute('aria-busy', snapshot.ariaBusy);
      }

      control.style.pointerEvents = snapshot.pointerEvents || '';
      control.classList.remove('loading');
      if (snapshot.text) setControlText(control, snapshot.text);
      loadingSnapshots.delete(control);
    });

    if (!isLoading) setOverlay(false);
  }

  function showVerifiedState(activeControl) {
    if (activeControl) setControlText(activeControl, VERIFIED_TEXT);
    setOverlay(true, VERIFIED_TEXT);
    recordDiagnostic('stock-check-verified');
  }

  async function fetchCart() {
    recordDiagnostic('cart-fetch-start');
    const response = await fetch(CART_JSON_URL, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error('Unable to load cart for inventory check.');
    const cart = await response.json();
    recordDiagnostic('cart-fetch-success', { itemCount: cart.items?.length || 0 });
    return cart;
  }

  async function checkSku(sku) {
    const normalizedSku = normalizeSku(sku);

    if (!normalizedSku) {
      recordDiagnostic('stock-check-missing-sku');
      return false;
    }
    if (stockCache.has(normalizedSku)) {
      recordDiagnostic('stock-check-cache-hit', { sku: normalizedSku, inStock: stockCache.get(normalizedSku) });
      return stockCache.get(normalizedSku);
    }

    const url = `${STOCK_PROXY_URL}?sku=${encodeURIComponent(normalizedSku)}`;
    recordDiagnostic('stock-check-request', { sku: normalizedSku, url });

    let response;
    try {
      response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
    } catch (error) {
      recordDiagnostic('stock-check-network-error', { sku: normalizedSku, message: error.message });
      throw new Error(`Inventory API request failed for SKU ${normalizedSku}: ${error.message}`);
    }

    const responseText = await response.text();

    if (!response.ok) {
      recordDiagnostic('stock-check-http-error', {
        sku: normalizedSku,
        status: response.status,
        body: responseText,
      });
      throw new Error(`Inventory API returned HTTP ${response.status} for SKU ${normalizedSku}.`);
    }

    let parsed;
    try {
      parsed = JSON.parse(responseText);
    } catch (error) {
      recordDiagnostic('stock-check-parse-error', { sku: normalizedSku, body: responseText, message: error.message });
      throw new Error(`Inventory API response was not valid JSON for SKU ${normalizedSku}.`);
    }

    if (typeof parsed !== 'boolean') {
      recordDiagnostic('stock-check-invalid-boolean', { sku: normalizedSku, parsed });
      throw new Error(`Inventory API response was not a boolean for SKU ${normalizedSku}.`);
    }

    const inStock = parsed === true;
    stockCache.set(normalizedSku, inStock);
    recordDiagnostic('stock-check-success', { sku: normalizedSku, inStock });
    return inStock;
  }

  async function findUnavailableItems(items) {
    const checksBySku = new Map();
    const itemsToCheck = items.filter(shouldCheckExternalStock);
    const trackedItems = items.filter((item) => item.inventoryTracked === true);
    const unknownItems = items.filter((item) => item.inventoryTracked === undefined);

    recordDiagnostic('stock-check-summary', {
      totalItems: items.length,
      externalCheckItems: itemsToCheck.length,
      shopifyTrackedItems: trackedItems.length,
      unknownTrackingItems: unknownItems.length,
    });

    trackedItems.forEach((item) =>
      recordDiagnostic('stock-check-skipped-shopify-tracked', {
        sku: normalizeSku(item.sku),
        variantId: item.variant_id || item.id,
        title: itemLabel(item),
      })
    );

    unknownItems.forEach((item) =>
      recordDiagnostic('stock-check-skipped-unknown-tracking', {
        sku: normalizeSku(item.sku),
        variantId: item.variant_id || item.id,
        title: itemLabel(item),
      })
    );

    itemsToCheck.forEach((item) => {
      const sku = normalizeSku(item.sku);
      if (!checksBySku.has(sku)) checksBySku.set(sku, checkSku(sku));
    });

    const results = await Promise.all(
      itemsToCheck.map(async (item) => ({
        item,
        inStock: await checksBySku.get(normalizeSku(item.sku)),
      }))
    );

    return results.filter(({ inStock }) => !inStock).map(({ item }) => item);
  }

  async function removeCartItems(items) {
    const updates = items.reduce((changes, item) => {
      if (item.key) changes[item.key] = 0;
      return changes;
    }, {});

    if (!Object.keys(updates).length) return;

    const response = await fetch(cartUpdateUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
    });

    if (!response.ok) throw new Error('Unable to remove unavailable cart items.');
  }

  async function removeMatchingCartItems(item) {
    const cart = await fetchCart();
    const unavailableCartItems = withCartLineMetadata(cart.items || []).filter((cartItem) => {
      const sameVariant = String(cartItem.variant_id) === String(item.id);
      const sameSku = normalizeSku(cartItem.sku) && normalizeSku(cartItem.sku) === normalizeSku(item.sku);

      return (sameVariant || sameSku) && cartItem.inventoryTracked !== true;
    });

    if (unavailableCartItems.length) await removeCartItems(unavailableCartItems);
    return unavailableCartItems;
  }

  function showUnavailableAlert(items, options = {}) {
    const removedFromCart = options.removedFromCart === true;
    const itemList = [...new Set(items.map(itemLabel))].map((label) => `- ${label}`).join('\n');
    const actionText = removedFromCart
      ? ` and ${items.length === 1 ? 'has' : 'have'} been removed from your cart`
      : '';

    window.alert(
      `The following item${items.length === 1 ? ' is' : 's are'} unavailable for delivery${actionText}:\n\n${itemList}`
    );
  }

  function showCheckFailedAlert() {
    window.alert(
      'We could not confirm inventory for your cart. Please try again before continuing to checkout.'
    );
  }

  function getSelectedProductItem(target) {
    const productInfo = target.closest('product-info') || document.querySelector('product-info');
    const productForm = target.closest('product-form') || productInfo?.querySelector('product-form');
    const form =
      target.closest('form[action*="/cart/add"]') ||
      productForm?.querySelector('form[action*="/cart/add"]') ||
      productInfo?.querySelector('form[action*="/cart/add"]');
    const variantInput = form?.querySelector('[name="id"]');
    const variantId = variantInput?.value;
    const variantJson = productInfo?.querySelector('variant-selects [data-selected-variant]')?.textContent;
    const fallbackInventoryTracked =
      productForm?.dataset.inventoryTracked === 'true' || variantInput?.dataset.inventoryTracked === 'true';
    let variant = null;

    if (variantJson) {
      try {
        variant = JSON.parse(variantJson);
      } catch (error) {
        console.warn('Unable to parse selected variant for inventory check.', error);
      }
    }

    if (!variant || (variantId && String(variant.id) !== String(variantId))) {
      const fallbackSku = normalizeSku(productForm?.dataset.selectedSku || variantInput?.dataset.sku);
      if (!variantId || !fallbackSku) return null;

      variant = {
        id: variantId,
        sku: fallbackSku,
        inventory_management: fallbackInventoryTracked ? 'shopify' : null,
        product_title:
          productForm?.dataset.productTitle || document.querySelector('h1')?.textContent?.trim() || 'This item',
        title: '',
      };
    }

    const quantity = Number(form?.querySelector('[name="quantity"]')?.value || 1);

    return {
      id: variant.id,
      sku: variant.sku,
      quantity,
      product_title: variant.product_title || document.querySelector('h1')?.textContent?.trim() || 'This item',
      variant_title: variant.title,
      inventoryTracked:
        variant.inventory_management === undefined ? fallbackInventoryTracked : Boolean(variant.inventory_management),
    };
  }

  function isProductCheckoutTarget(target) {
    return Boolean(
      target.closest('.shopify-payment-button') ||
        target.closest('[data-shopify="payment-button"]') ||
        target.closest('form[action*="/cart/add"] [name="checkout"]')
    );
  }

  function isCheckoutTarget(target) {
    return Boolean(target.closest(CHECKOUT_SELECTORS));
  }

  function stopEvent(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  async function validateCartCheckout() {
    const cart = await fetchCart();
    if (!cart.items?.length) {
      recordDiagnostic('cart-empty-skip-stock-check');
      window.alert('O carrinho está vazio.');
      return false;
    }

    const unavailableItems = await findUnavailableItems(withCartLineMetadata(cart.items || []));

    if (unavailableItems.length) {
      showUnavailableAlert(unavailableItems, { removedFromCart: true });
      await removeCartItems(unavailableItems);
      window.location.reload();
      return false;
    }

    return true;
  }

  async function validateProductCheckout(target) {
    const item = getSelectedProductItem(target);

    if (!item) {
      showCheckFailedAlert();
      return false;
    }

    const unavailableItems = await findUnavailableItems([item]);

    if (unavailableItems.length) {
      const removedItems = await removeMatchingCartItems(item);
      showUnavailableAlert(unavailableItems, { removedFromCart: removedItems.length > 0 });
      if (removedItems.length) window.location.reload();
      return false;
    }

    return true;
  }

  async function guardCheckout(event, mode) {
    if (allowNextAction) {
      allowNextAction = false;
      return;
    }

    if (validating) {
      stopEvent(event);
      return;
    }

    stopEvent(event);
    validating = true;
    activeCheckoutControl = event.submitter || event.target.closest(CHECKOUT_SELECTORS);
    setCheckoutLoadingState(true, activeCheckoutControl);

    try {
      const isAllowed =
        mode === 'product' ? await validateProductCheckout(event.target) : await validateCartCheckout();

      if (isAllowed) {
        if (window.MoagemSync?.flush) await window.MoagemSync.flush();
        allowNextAction = true;
        showVerifiedState(activeCheckoutControl);
        await delay(SUCCESS_DELAY_MS);
        setCheckoutLoadingState(false);
        resumeCheckout(event);
      }
    } catch (error) {
      console.error(error);
      showCheckFailedAlert();
    } finally {
      setCheckoutLoadingState(false);
      activeCheckoutControl = null;
      validating = false;
    }
  }

  function resumeCheckout(event) {
    const submitter = event.submitter || event.target.closest(CHECKOUT_SELECTORS);
    const form = submitter?.form || submitter?.closest('form');

    if (form) {
      if (typeof form.requestSubmit === 'function' && submitter?.tagName !== 'A') {
        form.requestSubmit(submitter);
      } else {
        form.submit();
      }
      return;
    }

    if (submitter?.href) {
      window.location.href = submitter.href;
      return;
    }

    if (submitter && typeof submitter.click === 'function') {
      submitter.click();
      return;
    }

    window.location.href = cartUrl();
  }

  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;
      const form = event.target;

      if (submitter?.name === 'checkout' || form?.action?.includes('/checkout')) {
        const mode = form?.action?.includes('/cart/add') ? 'product' : 'cart';
        guardCheckout(event, mode);
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      if (!isCheckoutTarget(event.target)) return;

      guardCheckout(event, isProductCheckoutTarget(event.target) ? 'product' : 'cart');
    },
    true
  );
})();
